/**
 * Single property marker on the map. Color encodes status/risk (green/yellow/red/gray).
 * Hover/tap shows compact property card; click "View property" navigates to /app/property/:id.
 */

import { useState } from 'react';
import { Marker } from 'react-map-gl/mapbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { PropertyMapHoverCard, type MapMarkerStatus } from './PropertyMapHoverCard';
import { cn } from '@/lib/utils';

export interface PropertyMapRow {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
  battery_kwh_total: number;
  usable_fraction: number;
  socPct: number;
  online: boolean;
  dataStale?: boolean;
  forecastRisk?: boolean;
  lastPollAt: string | null;
}

function markerStatus(row: PropertyMapRow): MapMarkerStatus {
  if (!row.online) return 'offline';
  if (row.dataStale || row.forecastRisk) return 'warning';
  return 'live';
}

function riskMessage(row: PropertyMapRow): string | null {
  if (!row.online) return 'Offline';
  if (row.dataStale) return 'Data stale';
  if (row.forecastRisk) return 'Overnight risk';
  return null;
}

function markerColorClass(status: MapMarkerStatus): string {
  switch (status) {
    case 'live':
      return 'bg-status-online';
    case 'warning':
      return 'bg-status-stale';
    case 'offline':
      return 'bg-status-offline';
    default:
      return 'bg-muted-foreground';
  }
}

interface PropertyMapMarkerProps {
  row: PropertyMapRow;
}

export function PropertyMapMarkerComponent({ row }: PropertyMapMarkerProps) {
  const [open, setOpen] = useState(false);
  const status = markerStatus(row);
  const remainingKwh =
    row.battery_kwh_total > 0
      ? row.battery_kwh_total * (row.socPct / 100) * row.usable_fraction
      : null;

  return (
    <Marker
      longitude={row.longitude}
      latitude={row.latitude}
      anchor="bottom"
      onClick={(e) => e.originalEvent.stopPropagation()}
    >
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              'h-4 w-4 rounded-full border-2 border-white shadow-md transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1',
              markerColorClass(status)
            )}
            aria-label={`${row.name} – ${status}`}
          />
        </PopoverTrigger>
        <PopoverContent
          side="top"
          align="center"
          sideOffset={8}
          className="w-auto p-0 border-border bg-transparent shadow-none"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <PropertyMapHoverCard
            propertyId={row.id}
            name={row.name}
            remainingKwh={remainingKwh}
            socPct={row.socPct}
            status={status}
            riskMessage={riskMessage(row)}
            lastPollAt={row.lastPollAt}
          />
        </PopoverContent>
      </Popover>
    </Marker>
  );
}
